import { api } from "@/api";
import type {
  CreditPackageVo,
  PlanVo,
  UserPlanVo,
  UserProfile,
  UserProfileUpdateRequest,
} from "@/api/Api";
import { logger } from "@/common/logger";
import { action, computed, makeObservable, observable } from "mobx";

type SetKeys = keyof Pick<
  UserStore,
  | "loading"
  | "user"
  | "userCredits"
  | "creditsInfo"
  | "plansInfo"
  | "freeCreditAmount"
  | "userPlan"
  | "isPricingModalOpen"
>;

export class UserStore {
  @observable user: UserProfile | null = null;
  @observable loading: boolean = false;
  @observable updateUserLoading: boolean = false;
  @observable userCredits: number = 0;
  @observable isPricingModalOpen: boolean = false;
  @observable creditsInfo: CreditPackageVo[] = [];
  @observable plansInfo: PlanVo[] = [];
  @observable freeCreditAmount: number = 0;
  @observable userPlan: UserPlanVo | null = null;

  constructor() {
    // 使用装饰器时仍需在 MobX 6 调用 makeAutoObservable/makeObservable 以启用响应式
    makeObservable(this);
  }

  @computed get isLogin() {
    return !!(this.user?.id || this.user?.email);
  }

  @computed get isFreeUser() {
    const code = this.userPlan?.planCode;

    return !code || code === "free_plan";
  }

  @action set<K extends SetKeys>(k: K, v: UserStore[K]) {
    this[k] = v as never;
  }

  @action setIsPricingModalOpen(v: boolean) {
    this.set("isPricingModalOpen", v);
  }

  @action async fetchUser(): Promise<UserProfile> {
    const res = await api.user.currentUserProfile();
    this.set("user", res.data);

    if (res.data?.id) {
      logger.setUser(res.data.id.toString());
    }

    return res.data;
  }

  @action async updateUser(data: UserProfileUpdateRequest) {
    if (this.updateUserLoading) {
      return;
    }

    this.updateUserLoading = true;

    try {
      await api.user.updateUserProfile(data);

      this.fetchUser();
    } finally {
      this.updateUserLoading = false;
    }
  }

  @action async fetchUserCredits() {
    const res = await api.credits.getUserCredit();
    this.set("userCredits", parseInt(res.data?.creditAmount || "0", 10));
  }

  @action async fetchUserPlan() {
    const res = await api.plans.queryUserPlan();
    this.set("userPlan", res.data);
  }

  @action async fetchCreditsInfo() {
    const res = await api.credits.getCredit();
    this.set("creditsInfo", res.data?.creditPackageList || []);
  }

  @action async fetchPlansInfo() {
    const res = await api.plans.queryActivePlans();
    this.set("plansInfo", res.data?.planList || []);
    this.set("freeCreditAmount", res.data?.freeCreditAmount || 100);
  }
}

export const userStore = new UserStore();
